import { QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  can,
  type Action,
  type Actor,
  type HierarchyDTO,
  type InviteDTO,
  type MemberDTO,
  type Resource,
  type TeamDTO,
  type UserSummary,
  type WorkspaceDTO,
} from '@regent/shared';
import { ApiError, api } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

export interface Me {
  user: UserSummary;
  workspaces: WorkspaceDTO[];
  /** Runs the platform: may open the admin console. */
  platformAdmin: boolean;
}

/** Query keys in one place so realtime handlers and mutations invalidate the right things. */
export const keys = {
  me: ['me'] as const,
  config: ['config'] as const,
  workspace: (ws: string) => ['w', ws] as const,
  hierarchy: (ws: string) => ['w', ws, 'hierarchy'] as const,
  members: (ws: string) => ['w', ws, 'members'] as const,
  invites: (ws: string) => ['w', ws, 'invites'] as const,
  teams: (ws: string) => ['w', ws, 'teams'] as const,
  grants: (ws: string, type: string, id: string) => ['w', ws, 'grants', type, id] as const,
};

export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await api.get<Me>('/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
});

export const configQuery = queryOptions({
  queryKey: keys.config,
  queryFn: () => api.get<{ googleEnabled: boolean; dev: boolean; appUrl: string }>('/config'),
  staleTime: Infinity,
});

export const hierarchyQuery = (ws: string) =>
  queryOptions({
    queryKey: keys.hierarchy(ws),
    queryFn: () => api.get<HierarchyDTO>(`/w/${ws}/hierarchy`),
  });

export const membersQuery = (ws: string) =>
  queryOptions({
    queryKey: keys.members(ws),
    queryFn: async () => (await api.get<{ members: MemberDTO[] }>(`/w/${ws}/members`)).members,
    staleTime: 60_000,
  });

export const teamsQuery = (ws: string) =>
  queryOptions({
    queryKey: keys.teams(ws),
    queryFn: async () => (await api.get<{ teams: TeamDTO[] }>(`/w/${ws}/teams`)).teams,
    staleTime: 60_000,
  });

export const invitesQuery = (ws: string) =>
  queryOptions({
    queryKey: keys.invites(ws),
    queryFn: async () => (await api.get<{ invites: InviteDTO[] }>(`/w/${ws}/invites`)).invites,
  });

// ---------- Hooks ----------

export function useMe(): Me | null | undefined {
  return useQuery(meQuery).data;
}

/** The workspace id from the URL. Only valid under /w/$workspaceId. */
export function useWorkspaceId(): string {
  return useParams({ strict: false }).workspaceId as string;
}

export function useWorkspace(): WorkspaceDTO | undefined {
  const id = useWorkspaceId();
  return useMe()?.workspaces.find((w) => w.id === id);
}

export function useHierarchy() {
  return useQuery(hierarchyQuery(useWorkspaceId()));
}

export function useMembers() {
  return useQuery(membersQuery(useWorkspaceId()));
}

export function useTeams() {
  return useQuery(teamsQuery(useWorkspaceId()));
}

/** Lookup of members by user id, for rendering assignees, authors and mentions. */
export function useMemberMap(): Map<string, MemberDTO> {
  const { data } = useMembers();
  return new Map((data ?? []).map((m) => [m.id, m]));
}

export function useActor(): Actor | null {
  const me = useMe();
  const workspace = useWorkspace();
  const members = useMembers().data;
  if (!me || !workspace) return null;
  const teamIds = members?.find((m) => m.id === me.user.id)?.teamIds ?? [];
  return { userId: me.user.id, role: workspace.role, limited: workspace.limited, teamIds, platformAdmin: me.platformAdmin };
}

/** Client side permission check, same helper the server enforces with. Used to hide UI. */
export function useCan(action: Action, resource?: Resource | null): boolean {
  return can(useActor(), action, resource);
}

/** Small wrapper: run a mutation, then invalidate the given query keys. */
export function useApiMutation<TInput, TOutput = unknown>(
  fn: (input: TInput) => Promise<TOutput>,
  invalidate: readonly (readonly unknown[])[] = [],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });
}
